import { NgClass } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    effect,
    ElementRef,
    inject,
    input,
    model,
    OnDestroy,
    OnInit,
    output,
    untracked,
    viewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormsModule } from '@angular/forms';

import { BaasI18n } from '@lvflanders/bar';

import { BaasActionButtonOptions } from '../baas-action-button/baas-action-button.types';
import { BaasConfirmService } from '../baas-confirm/baas-confirm.service';
import { BaasConfirmOptions } from '../baas-confirm/baas-confirm.types';
import { BaasLayoutService } from '../layout';
import { BaasRemark } from '../remarks/baas-remark/baas-remark';
import { BaasRemarkList } from '../remarks/baas-remark-list/baas-remark-list';
import { BaasToastMessageService } from '../toast-messages/baas-toast-message-service';
import { BaasEditService } from './baas-edit.service';
import {
    BaasEditConfirmSave,
    BaasEditSaveOptions,
    BaasEditSaveOutcome,
    BaasEditSaveReason,
    BaasEditSaveResult,
    BaasEditSaveStatus,
    BaasEditValidation
} from './baas-edit.types';
import { BaasEditLoader } from './loader/baas-edit-loader';

@Component({
    selector: 'baas-edit',
    templateUrl: './baas-edit.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgClass, FormsModule, BaasRemarkList, BaasEditLoader, BaasRemark]
})
/**
 * BaaS edit page component supporting asynchronous operations and signals with OnPush.
 * This integrates with the new layout. */
export class BaasEdit implements OnInit, OnDestroy {
    readonly #layout = inject(BaasLayoutService);
    readonly #destroyRef = inject(DestroyRef);
    readonly #toastMessageService = inject(BaasToastMessageService);
    readonly #i18n = inject(BaasI18n);
    readonly #confirm = inject(BaasConfirmService);
    readonly #service = inject(BaasEditService, { optional: true });

    readonly formRef = viewChild<ElementRef<HTMLElement>>('formRef');

    #pendingSaveRequests: ((value: BaasEditSaveResult) => void)[] = [];

    /** The reactive forms control (can be a group or a plain control). */
    readonly form = input<AbstractControl>(undefined);
    /** The status of the save operation. */
    readonly saveStatus = input<BaasEditSaveStatus>();
    /** Whether still loading. */
    readonly loading = input<boolean>();
    /** Whether pending. */
    readonly pending = input<boolean>();
    /** The error validations. */
    readonly validations = input<BaasEditValidation[]>();
    /** Whether readonly. */
    readonly readonly = input<boolean>(false);
    /** Whether the undo must be confirmed. */
    readonly confirmUndo = input<boolean>(false);
    /** Undo confirmation options. */
    readonly confirmUndoOptions = input<BaasConfirmOptions>();
    /** Whether to show an undo message after doing the undo. */
    readonly showUndoMessage = input<boolean>(false);
    /** Undo message to show if enabled. */
    readonly undoMessage = input<string>(this.#i18n.get('DOMAIN_BAR_LAYOUT.EDIT_PAGE.MESSAGE.UNDO'));
    /** Undo tooltip. */
    readonly undoTooltip = input<string>(this.#i18n.get('DOMAIN_BAR_LAYOUT.EDIT_PAGE.ACTION.UNDO'));
    /** Undo content. */
    readonly undoContent = input<string>(undefined);
    /** Whether to show the undo content together with the icon. */
    readonly showUndoContent = input<boolean>(undefined);
    /** Whether the save must be confirmed. */
    readonly confirmSave = input<boolean | BaasEditConfirmSave>(false);
    readonly confirmSaveOptions = input<BaasConfirmOptions>();
    /** Defines whether the autosave navigation should be blocked (meaning you have to confirm) if saving needs to be confirmed.
     * Use in combination with [confirmSave] property.*/
    readonly confirmAutosaveBlocking = input<boolean>(true);
    /** Options specific for autosave confirmation. */
    readonly confirmAutosaveOptions = input<BaasConfirmOptions>(undefined);
    /** Whether to show a save message after doing a save. */
    readonly showSaveMessage = input<boolean>(true);
    /** Save message to show if enabled. */
    readonly saveMessage = input<string>(this.#i18n.get('DOMAIN_BAR_LAYOUT.EDIT_PAGE.MESSAGE.SAVE'));
    /** Undo tooltip. */
    readonly saveTooltip = input<string>(this.#i18n.get('DOMAIN_BAR_LAYOUT.EDIT_PAGE.ACTION.SAVE'));
    /** Save content. */
    readonly saveContent = input<string>(undefined);
    /** Whether to show the save content together with the icon. */
    readonly showSaveContent = input<boolean>(undefined);
    /** Whether valid when pristine. This means if invalid we consider it valid in case it is pristine. */
    readonly validWhenPristine = input<boolean>(false);
    /** Whether the before unload event, in case the user navigates away (href), is disabled. */
    readonly beforeUnloadDisabled = input<boolean>(false);
    /** Whether to show an extra warning when the form is invalid and a save occurs. */
    readonly showSaveMessageOnInvalid = input<boolean>(true);
    /** Whether valid (optional). */
    readonly valid = model<boolean>(undefined);
    /** Whether pristine (optional). */
    readonly pristine = model<boolean>(undefined);
    /** Whether disabled (optional). */
    readonly disabled = model<boolean>(undefined);
    readonly submitted = model<boolean>(false);
    /** Occurs when undo is triggered. */
    readonly undo = output();
    /** Occurs when save is triggered. */
    readonly save = output<BaasEditSaveOptions>();
    /** Occurs when validation is requested. */
    readonly validating = output();

    readonly #undoContent = computed(() => {
        return this.#showUndoContent() ? this.undoContent() || this.#i18n.get('DOMAIN_BAR_LAYOUT.EDIT_PAGE.ACTION.UNDO') : undefined;
    });
    readonly #saveContent = computed(() => {
        return this.#showSaveContent() ? this.saveContent() || this.#i18n.get('DOMAIN_BAR_LAYOUT.EDIT_PAGE.ACTION.SAVE') : undefined;
    });
    readonly #showSaveContent = computed<boolean>(() => this.showSaveContent() === true || (this.showSaveContent() === undefined && !!this.saveContent()));
    readonly #showUndoContent = computed<boolean>(() => this.showUndoContent() === true || (this.showUndoContent() === undefined && !!this.undoContent()));

    readonly #valid = computed(() => this.valid() || (this.validWhenPristine() && this.pristine()));
    readonly #invalid = computed(() => !this.#valid());
    readonly #contentActionsDisabled = computed(() => this.#nothingToSave() || this.saveStatus() === 'pending');
    readonly #contentActionsHidden = computed(() => this.readonly());
    readonly #showLoader = computed(() => this.loading() || this.pending());
    readonly #showRemarkList = computed(() => this.submitted() && this.validations().length);
    readonly #nothingToSave = computed(() => this.pristine() || this.disabled() || this.readonly());

    protected readonly viewModel = computed(() => {
        return {
            showLoader: this.#showLoader(),
            showRemarkList: this.#showRemarkList(),
            validations: this.validations(),
            loading: this.loading(),
            submitted: this.submitted()
        };
    });

    constructor() {
        effect(() => {
            const saveStatus = this.saveStatus();
            untracked(() => {
                switch (saveStatus) {
                    case 'fulfilled': {
                        this.#markAsPristine();
                        this.submitted.set(false);
                        if (this.showSaveMessage()) {
                            this.#toastMessageService.addMessage({
                                type: 'success',
                                text: this.saveMessage()
                            });
                        }
                        this.#completeSaveRequests('saved');
                        break;
                    }
                    case 'error': {
                        this.#completeSaveRequests('error');
                        break;
                    }
                }
            });
        });

        effect(() => {
            const disabled = this.disabled();
            untracked(() => {
                const form = this.form();
                if (form && form.disabled !== disabled) {
                    if (disabled) form.disable();
                    else form.enable();
                }
            });
        });

        effect(() => {
            const formRef = this.formRef();
            if (formRef) {
                // add a hidden button for screenreaders (web toegankelijkheid)
                const btn = document.createElement('button');
                btn.type = 'submit';
                btn.className = 'sr-only';
                btn.tabIndex = -1;
                btn.innerText = this.#i18n.get('DOMAIN_BAR_LAYOUT.EDIT_PAGE.ACTION.SAVE');
                btn.setAttribute('aria-hidden', 'true');
                btn.onclick = () => this.#save({ reason: 'regular' });
                formRef.nativeElement.appendChild(btn);
            }
        });

        effect(() => this.#layout.contentActions.set(this.#contentActions()));
    }

    ngOnInit(): void {
        if (this.#service) {
            // register callback when save is requested
            this.#service._saveRequested = (value) => {
                this.#pendingSaveRequests.push(value.resolve);
                if (this.saveStatus() !== 'pending') this.#save(value.options);
            };
        }
        if (this.form()) {
            const assignFormSignals = () => {
                this.valid.set(this.form().valid);
                this.pristine.set(this.form().pristine);
                this.disabled.set(this.form().disabled);
            };

            assignFormSignals();

            this.form()
                .events.pipe(takeUntilDestroyed(this.#destroyRef))
                .subscribe((_event) => assignFormSignals());
        }

        this.#defineBeforeUnload();
    }

    ngOnDestroy(): void {
        this.#layout.contentActions.set([]);

        window.onbeforeunload = null;

        if (this.#service) this.#service._saveRequested = undefined;

        this.#completeSaveRequests('destroy');
    }

    readonly #contentActions = computed(() => {
        return [
            {
                icon: {
                    class: 'fas fa-save',
                    iconOnly: !this.#showSaveContent(),
                    srText: this.saveTooltip()
                },
                hidden: this.#contentActionsHidden(),
                disabled: this.#contentActionsDisabled(),
                action: () => this.#save({ reason: 'regular' }),
                tooltip: this.saveTooltip(),
                content: this.#saveContent()
            },
            {
                icon: {
                    class: 'fas fa-undo',
                    iconOnly: !this.#showUndoContent(),
                    srText: this.undoTooltip()
                },
                hidden: this.#contentActionsHidden(),
                disabled: this.#contentActionsDisabled(),
                action: () => this.#undo(),
                tooltip: this.undoTooltip(),
                content: this.#undoContent()
            }
        ] as BaasActionButtonOptions[];
    });

    #undo(): void {
        const undo = () => {
            this.undo.emit();
            this.#markAsPristine();
            this.submitted.set(false);
            if (this.showUndoMessage()) {
                this.#toastMessageService.addMessage({
                    type: 'info',
                    text: this.undoMessage()
                });
            }
        };
        if (this.confirmUndo()) {
            const confirmOptions = this.confirmUndoOptions() ? this.confirmUndoOptions() : {};
            if (!confirmOptions.message) confirmOptions.message = this.#i18n.get('DOMAIN_BAR_LAYOUT.EDIT_PAGE.ARE_YOU_SURE_UNDO');
            if (!confirmOptions.title) confirmOptions.title = this.#i18n.get('DOMAIN_BAR_LAYOUT.EDIT_PAGE.ARE_YOU_SURE');
            this.#confirm.confirm(confirmOptions).subscribe(() => undo());
        } else undo();
    }

    #skipSave() {
        this.#completeSaveRequests('nothingToSave');
    }

    #save(options: BaasEditSaveOptions): void {
        const save = () => {
            this.submitted.set(true);
            this.validating.emit();
            if (this.#valid()) this.save.emit(options);
            else {
                // show an extra message why validation has failed
                this.#tryEmitSaveMessageOnInvalid(options.reason);
                this.#completeSaveRequests('validation');
            }
        };
        if (this.#nothingToSave()) {
            this.#completeSaveRequests('nothingToSave');
        } else {
            if (this.confirmSave() === true || this.confirmSave() === 'always' || (options.reason === 'autosave' && this.confirmSave() === 'autosave')) {
                const confirmOptions =
                    options.reason === 'autosave' && this.confirmAutosaveOptions() ? this.confirmAutosaveOptions() : this.confirmSaveOptions() || {};

                const nonBlockingAutosave = options.reason === 'autosave' && !this.confirmAutosaveBlocking();
                if (!confirmOptions.message) {
                    if (nonBlockingAutosave) confirmOptions.message = this.#i18n.get('DOMAIN_BAR_LAYOUT.EDIT_PAGE.ARE_YOU_SURE_AUTOSAVE_NON_BLOCKING');
                    else confirmOptions.message = this.#i18n.get('DOMAIN_BAR_LAYOUT.EDIT_PAGE.ARE_YOU_SURE_SAVE');
                }
                if (!confirmOptions.title) {
                    if (nonBlockingAutosave) confirmOptions.title = this.#i18n.get('DOMAIN_BAR_LAYOUT.EDIT_PAGE.CONFIRM_TITLE_NON_BLOCKING');
                    else confirmOptions.title = this.#i18n.get('DOMAIN_BAR_LAYOUT.EDIT_PAGE.ARE_YOU_SURE');
                }

                if (nonBlockingAutosave && !confirmOptions.refuteText)
                    confirmOptions.refuteText = this.#i18n.get('DOMAIN_BAR_LAYOUT.EDIT_PAGE.CONFIRM_FALSE_NON_BLOCKING');
                if (nonBlockingAutosave && !confirmOptions.confirmText)
                    confirmOptions.confirmText = this.#i18n.get('DOMAIN_BAR_LAYOUT.EDIT_PAGE.CONFIRM_TRUE_NON_BLOCKING');

                if (!nonBlockingAutosave) this.#confirm.confirm(confirmOptions).subscribe(() => save());
                else {
                    this.#confirm.open(confirmOptions).subscribe((confirm) => {
                        // NOTE watch out can also be undefined (in this case we do nothing)
                        if (confirm === true) save();
                        else if (confirm === false) this.#skipSave();
                    });
                }
            } else save();
        }
    }

    #markAsPristine() {
        if (this.form()) {
            this.form().markAsPristine();
            this.form().markAsUntouched();
        } else this.pristine.set(true);
    }

    #defineBeforeUnload(): void {
        window.onbeforeunload = (e: BeforeUnloadEvent) => {
            // in case nothing changed or not active just return
            if (this.beforeUnloadDisabled() || this.#nothingToSave()) {
                return;
            }
            e.preventDefault();
            return this.#i18n.get('DOMAIN_BAR_LAYOUT.EDIT_PAGE.MESSAGE.LEAVE_PAGE');
        };
    }

    #completeSaveRequests(outcome: BaasEditSaveOutcome): void {
        const success = outcome === 'saved' || outcome === 'nothingToSave';
        this.#pendingSaveRequests.forEach((resolve) => resolve({ success, outcome }));
        this.#pendingSaveRequests = [];
    }

    #tryEmitSaveMessageOnInvalid(reason: BaasEditSaveReason): void {
        if (this.showSaveMessageOnInvalid()) {
            this.#toastMessageService.addMessage({
                text: this.#i18n.get('DOMAIN_BAR_LAYOUT.EDIT_PAGE.MESSAGE.' + (reason === 'autosave' ? 'INVALID_AUTOSAVE' : 'INVALID')),
                type: 'error'
            });
        }
    }
}
